import { useEffect, useRef, memo } from "react";
import * as THREE from "three";

/**
 * Fixed-position Three.js canvas with:
 * - Deep star field (1800 points on a sphere shell, far background)
 * - Particle field (500 points, floating squares in foreground)
 * - Neural-net connection lines
 * - Rotating wireframe torus & icosahedron
 * - Small spiral galaxy
 * - Shooting stars
 * - Mouse-parallax camera drift
 */
const ThreeBackground = memo(({ isDark }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // â”€â”€ Renderer â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000,
    );
    camera.position.z = 30;

    // -- Color palette (shared by stars + particles) --------------------------
    const c1 = new THREE.Color(isDark ? 0x00e5ff : 0x2563eb);
    const c2 = new THREE.Color(isDark ? 0xa855f7 : 0x7c3aed);

    // -- Star field (deep background sphere shell) ----------------------------
    const STAR_COUNT = 1800;
    const starPos = new Float32Array(STAR_COUNT * 3);
    const starCol = new Float32Array(STAR_COUNT * 3);
    const wC = new THREE.Color(0xd8f0ff);
    for (let i = 0; i < STAR_COUNT; i++) {
      const r = 80 + Math.random() * 380;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      starPos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      starPos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      starPos[i * 3 + 2] = r * Math.cos(phi);
      const m = Math.random();
      const sc = m < 0.55 ? wC : m < 0.78 ? c1 : c2;
      starCol[i * 3] = sc.r;
      starCol[i * 3 + 1] = sc.g;
      starCol[i * 3 + 2] = sc.b;
    }
    const starGeo = new THREE.BufferGeometry();
    starGeo.setAttribute("position", new THREE.BufferAttribute(starPos, 3));
    starGeo.setAttribute("color", new THREE.BufferAttribute(starCol, 3));
    const stars = new THREE.Points(
      starGeo,
      new THREE.PointsMaterial({
        size: 0.34,
        vertexColors: true,
        transparent: true,
        opacity: isDark ? 0.8 : 0.42,
        sizeAttenuation: true,
      }),
    );
    scene.add(stars);

    // -- Particles (floating squares, foreground) ---------------------------------
    const COUNT = 500;
    const pos = new Float32Array(COUNT * 3);
    const cols = new Float32Array(COUNT * 3);

    for (let i = 0; i < COUNT; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 100;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 100;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 60;
      const c = c1.clone().lerp(c2, Math.random());
      cols[i * 3] = c.r;
      cols[i * 3 + 1] = c.g;
      cols[i * 3 + 2] = c.b;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    geo.setAttribute("color", new THREE.BufferAttribute(cols, 3));
    const mat = new THREE.PointsMaterial({
      size: 0.25,
      vertexColors: true,
      transparent: true,
      opacity: isDark ? 0.55 : 0.45,
      sizeAttenuation: true,
    });
    const particles = new THREE.Points(geo, mat);
    scene.add(particles);

    // â”€â”€ Connection lines â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const lineMat = new THREE.LineBasicMaterial({
      color: isDark ? 0x00e5ff : 0x2563eb,
      transparent: true,
      opacity: isDark ? 0.04 : 0.03,
    });
    const lineGroup = new THREE.Group();
    const nodes = Array.from(
      { length: 40 },
      () =>
        new THREE.Vector3(
          (Math.random() - 0.5) * 80,
          (Math.random() - 0.5) * 60,
          (Math.random() - 0.5) * 30,
        ),
    );
    nodes.forEach((a, i) =>
      nodes.slice(i + 1).forEach((b) => {
        if (a.distanceTo(b) < 16)
          lineGroup.add(
            new THREE.Line(
              new THREE.BufferGeometry().setFromPoints([a, b]),
              lineMat,
            ),
          );
      }),
    );
    scene.add(lineGroup);

    // â”€â”€ Torus â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const torusMat = new THREE.MeshBasicMaterial({
      color: isDark ? 0x00e5ff : 0x2563eb,
      wireframe: true,
      transparent: true,
      opacity: isDark ? 0.07 : 0.05,
    });
    const torus = new THREE.Mesh(
      new THREE.TorusGeometry(8, 0.7, 16, 80),
      torusMat,
    );
    torus.position.set(28, -10, -15);
    scene.add(torus);

    const icoMat = new THREE.MeshBasicMaterial({
      color: isDark ? 0xa855f7 : 0x7c3aed,
      wireframe: true,
      transparent: true,
      opacity: isDark ? 0.09 : 0.06,
    });
    const ico = new THREE.Mesh(new THREE.IcosahedronGeometry(5, 1), icoMat);
    ico.position.set(-28, 12, -20);
    scene.add(ico);

    // â”€â”€ Small spiral galaxy (top-right, tucked away) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // 300 particles in 2 arms; scaled small so it reads as a distant nebula
    const GAL_N = 300;
    const galPos = new Float32Array(GAL_N * 3);
    const galCol = new Float32Array(GAL_N * 3);
    for (let i = 0; i < GAL_N; i++) {
      const arm = i % 2;
      const prog = i / GAL_N;
      const angle = prog * Math.PI * 3.2;
      const r = 0.5 + angle * 1.1;
      const armOff = (arm / 2) * Math.PI * 2;
      const scatter = (Math.random() - 0.5) * (0.3 + r * 0.08);
      galPos[i * 3] = r * Math.cos(angle + armOff) + scatter;
      galPos[i * 3 + 1] = (Math.random() - 0.5) * 0.3;
      galPos[i * 3 + 2] = r * Math.sin(angle + armOff) + scatter;
      const frac = Math.min(r / 11, 1);
      const gc = c1.clone().lerp(c2, frac);
      galCol[i * 3] = gc.r;
      galCol[i * 3 + 1] = gc.g;
      galCol[i * 3 + 2] = gc.b;
    }
    const galGeo = new THREE.BufferGeometry();
    galGeo.setAttribute("position", new THREE.BufferAttribute(galPos, 3));
    galGeo.setAttribute("color", new THREE.BufferAttribute(galCol, 3));
    const galaxy = new THREE.Points(
      galGeo,
      new THREE.PointsMaterial({
        size: 0.22,
        vertexColors: true,
        transparent: true,
        opacity: isDark ? 0.7 : 0.45,
        sizeAttenuation: true,
      }),
    );
    galaxy.rotation.x = -0.5;
    galaxy.position.set(22, 14, -10); // top-right, behind other elements
    scene.add(galaxy);

    // -- Shooting stars -------------------------------------------------------
    // Camera z=30, FOV=75. Visible at z=0: x~+-40, y~+-23.
    const TRAIL = 20;
    const headC = new THREE.Color(isDark ? 0xb8f0ff : 0x1a6fa8);
    const tailFade = isDark ? 0 : 1; // dark→fade to black, light→fade to white

    const makeComet = (initDelay) => {
      const pArr = new Float32Array(TRAIL * 3).fill(9999);
      const cArr = new Float32Array(TRAIL * 3);
      const g = new THREE.BufferGeometry();
      g.setAttribute("position", new THREE.BufferAttribute(pArr, 3));
      g.setAttribute("color", new THREE.BufferAttribute(cArr, 3));
      const line = new THREE.Line(
        g,
        new THREE.LineBasicMaterial({
          vertexColors: true,
          transparent: true,
          opacity: isDark ? 0.7 : 0.45,
        }),
      );
      line.frustumCulled = false;
      line.visible = false;
      scene.add(line);
      return {
        line,
        active: false,
        hx: 0,
        hy: 0,
        dx: 0,
        dy: 0,
        speed: 0,
        tailLen: 0,
        cooldown: initDelay,
      };
    };

    // 2 comets, one every ~40-80 s — rare treat
    const comets = [
      makeComet(900 + Math.floor(Math.random() * 600)), // first at ~15-25 s
      makeComet(2400 + Math.floor(Math.random() * 1200)), // second at ~40-60 s
    ];

    const spawnComet = (c) => {
      // 50 % chance left-to-right, 50 % right-to-left
      const goRight = Math.random() < 0.5;
      const ang = 0.14 + Math.random() * 0.24; // 8-22 deg below horizontal
      if (goRight) {
        c.hx = -46 - Math.random() * 4; // off-screen left
        c.hy = 10 + Math.random() * 12; // upper half, random height
        c.dx = Math.cos(ang);
        c.dy = -Math.sin(ang);
      } else {
        c.hx = 46 + Math.random() * 4; // off-screen right
        c.hy = 10 + Math.random() * 12;
        c.dx = -Math.cos(ang);
        c.dy = -Math.sin(ang);
      }
      c.speed = 0.12 + Math.random() * 0.08; // 0.12-0.20 u/frame
      c.tailLen = 6 + Math.random() * 5; // 6-11 units
      c.active = true;
      c.line.visible = true;
    };

    const tickComet = (c) => {
      if (!c.active) {
        if (--c.cooldown <= 0) spawnComet(c);
        return;
      }
      c.hx += c.dx * c.speed;
      c.hy += c.dy * c.speed;
      // Kill only when the TAIL TIP has also left the visible area
      const tailTipX = c.hx - c.dx * c.tailLen;
      const tailTipY = c.hy - c.dy * c.tailLen;
      const dead =
        (c.dx > 0 ? tailTipX > 46 : tailTipX < -46) || tailTipY < -27;
      if (dead) {
        c.active = false;
        c.line.visible = false;
        c.cooldown = 2400 + Math.floor(Math.random() * 2400); // 40-80 s gap
        return;
      }
      const pa = c.line.geometry.attributes.position;
      const ca = c.line.geometry.attributes.color;
      for (let i = 0; i < TRAIL; i++) {
        const f = i / (TRAIL - 1); // 0=head, 1=tail
        pa.array[i * 3] = c.hx - c.dx * f * c.tailLen;
        pa.array[i * 3 + 1] = c.hy - c.dy * f * c.tailLen;
        pa.array[i * 3 + 2] = 1;
        const b = Math.pow(1 - f, 2.2); // gentler fade, visible tail
        ca.array[i * 3] = headC.r * b + tailFade * (1 - b);
        ca.array[i * 3 + 1] = headC.g * b + tailFade * (1 - b);
        ca.array[i * 3 + 2] = headC.b * b + tailFade * (1 - b);
      }
      pa.needsUpdate = true;
      ca.needsUpdate = true;
    };

    // â”€â”€ Mouse parallax â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    let mx = 0,
      my = 0;
    const onMouse = (e) => {
      mx = (e.clientX / window.innerWidth - 0.5) * 2;
      my = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener("mousemove", onMouse);

    // â”€â”€ Resize â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener("resize", onResize);

    // â”€â”€ Animation loop â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    let t = 0,
      animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      t += 0.004;

      particles.rotation.y = t * 0.05;
      particles.rotation.x = Math.sin(t * 0.3) * 0.02;
      torus.rotation.x = t * 0.3;
      torus.rotation.y = t * 0.2;
      ico.rotation.x = t * 0.25;
      ico.rotation.z = t * 0.15;
      lineGroup.rotation.y = t * 0.03;
      galaxy.rotation.y = t * 0.06;
      stars.rotation.y = t * 0.006;

      camera.position.x += (mx * 3 - camera.position.x) * 0.03;
      camera.position.y += (-my * 2 - camera.position.y) * 0.03;

      comets.forEach(tickComet);

      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("mousemove", onMouse);
      window.removeEventListener("resize", onResize);
      renderer.dispose();
    };
  }, [isDark]);

  return <canvas ref={canvasRef} id="three-canvas" />;
});

export default ThreeBackground;
